import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { smsModules, ModuleFeature } from "../smsModulesData";
import { smsFeatureIconMap } from "../smsModuleIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => {
  const Icon = smsFeatureIconMap[feature.icon];

  return (
    <div className="sms-module-feature">
      <div className="sms-module-feature__head">
        <span className="sms-module-feature__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="sms-module-feature__title">{feature.title}</h4>
      </div>
    {feature.description && <p className="sms-module-feature__desc">{feature.description}</p>}
    {feature.items && (
      <ul className="sms-module-feature__list">
        {feature.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )}
    {feature.subsections?.map((sub) => (
      <div className="sms-module-feature__sub" key={sub.title}>
        <h5>{sub.title}</h5>
        <ul className="sms-module-feature__list">
          {sub.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
      ))}
    </div>
  );
};

const SmsModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(smsModules[0].id);
  const activeModule = smsModules.find((m) => m.id === activeId) ?? smsModules[0];

  return (
    <section className="sms-modules" aria-labelledby="sms-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="sms-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            System Modules
          </h2>
          <p className="sms-modules__lead">
            Separate modules for students, parents, teachers, accountants, librarians, and
            administrators — each with role-specific functionalities.
          </p>
        </AnimatedSection>

        <div className="sms-modules__tabs" role="tablist" aria-label="School management modules">
          {smsModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`sms-panel-${mod.id}`}
              id={`sms-tab-${mod.id}`}
              className={`sms-modules__tab ${activeId === mod.id ? "sms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {mod.title.replace(" Module", "")}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`sms-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`sms-tab-${activeModule.id}`}
            className="sms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="sms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SmsModulesSection;
